import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRemoveMember } from "@/features/clients/hooks/use-clients";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import type { ClientMemberWithUser } from "@/features/clients/types";
import { useTranslation } from "@/features/utils/i18n";
import { DotsThreeOutlineIcon, TrashIcon } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

/**
 * Row actions on the team table. Only one: take somebody off the team.
 *
 * Promoting, demoting and revoking are things you do to an *access*, and they
 * live on the Utilisateurs page now — a member is not a login, and most of the
 * rows here have none to change.
 */
export const MemberAction = ({ member }: { member: ClientMemberWithUser }) => {
  const { t } = useTranslation("members");
  const { company: clientId } = useParams<{ company: string }>();
  const removeMember = useRemoveMember();
  const isOwner = useHasRole("OWNER");

  if (!isOwner) return null;

  const handleRemove = async () => {
    if (!clientId) return;

    try {
      await removeMember.mutateAsync({
        clientId,
        memberId: member.membership.id,
      });
      toast.success(t("messages.memberRemoved"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.removeError"),
      );
    }
  };

  return (
    <div className="text-right">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">{t("common.openMenu")}</span>
            <DotsThreeOutlineIcon weight="fill" className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Confirmed, not immediate: the row carries the member's schedule,
              calendars and transfer settings, and taking them off the team
              changes what the agent says on the next call. */}
          <DropdownMenuItem className="p-0">
            <Button
              variant="ghost"
              onClick={handleRemove}
              className="text-destructive hover:text-destructive w-full justify-start"
              confirm={t("messages.removeMemberConfirm", {
                name: member.membership.display_name || member.user?.email || "",
              })}
            >
              <TrashIcon className="h-4 w-4" weight="fill" />
              {t("actions.removeFromTeam")}
            </Button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
